import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { DefaultTheme } from 'styled-components';
import renderNavbarLinks, { NavbarLinksType } from '@helpers/get-navbar-links';
import renderMobileNavbarLinks from '@helpers/get-mobile-navbar-links';
import Wrapper from '@components/wrapper.styled';
import { debounce } from '@helpers/debounce';
import ErrorBoundary from '@components/error-boundary';
import { ImageProps } from '@components/image';
import {
  HeaderWrapper,
  HeaderBgColor,
  StyledHeader,
  Content,
  Menu,
  MainActionsWrapper,
  MobileContactButton,
  Logo,
  LogoWrapper,
} from './header.styled';
import {
  BurgerButton,
  Line,
  MobileMenu,
  MobileContent,
  ButtonWrapper,
} from './mobile-header.styled';

interface HeaderQueryInterface {
  prismicGeneralConfig: {
    data: {
      logo: ImageProps;
      navbar_links: NavbarLinksType[];
    };
  };
}

export const query = graphql`
  query HeaderQuery {
    prismicGeneralConfig {
      data {
        logo {
          gatsbyImageData
          url
          alt
        }
        navbar_links {
          link_label
          link_url {
            url
            target
            document {
              ... on PrismicPage {
                uid
              }
              ... on PrismicBlogIndex {
                uid
              }
              ... on PrismicGuidesIndex {
                uid
              }
              ... on PrismicCaseStudiesIndex {
                uid
              }
              ... on PrismicContact {
                uid
              }
            }
          }
          dropdown_menu {
            document {
              ... on PrismicDropdownMenu {
                uid
                data {
                  title {
                    text
                  }
                  body {
                    ... on PrismicDropdownMenuDataBodyLinks {
                      primary {
                        group_title {
                          text
                        }
                      }
                      items {
                        link {
                          url
                          document {
                            ... on PrismicPage {
                              uid
                            }
                            ... on PrismicBlogIndex {
                              uid
                            }
                            ... on PrismicGuidesIndex {
                              uid
                            }
                            ... on PrismicCaseStudiesIndex {
                              uid
                            }
                          }
                        }
                        link_label
                        description
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

interface HeaderProps {
  theme: DefaultTheme;
}

const Header = memo(({ theme }: HeaderProps): JSX.Element => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [show, setShow] = useState(false);
  const headerRef = useRef<HTMLHeadingElement>(null);

  const {
    prismicGeneralConfig: {
      data: { logo, navbar_links },
    },
  }: HeaderQueryInterface = useStaticQuery(query);

  const headerHeight = headerRef?.current?.offsetHeight;

  const handleScroll = useCallback(
    debounce(() => {
      if (window.scrollY > 100) {
        setShow(true);
      } else {
        setShow(false);
      }
    }, 250),
    []
  );

  const toggleMenu = useCallback(() => {
    setIsMenuVisible((prev) => !prev);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [handleScroll]);

  const renderLogo = useCallback(() => {
    if (!logo.url) return null;
    return (
      <a href="/" id="logo-navbar-link" aria-label="Navigate to the Homepage">
        <Logo
          gatsbyImageData={logo?.gatsbyImageData}
          url={logo?.url}
          alt={logo?.alt}
          loading="lazy"
          objectFit="contain"
          objectPosition="0% 0%"
        />
      </a>
    );
  }, [logo.url]);

  return (
    <>
      <StyledHeader
        $isActive={isMenuVisible}
        $visible={show}
        data-position={show ? 'fixed' : 'absolute'}
      >
        <HeaderWrapper>
          <Content>
            <LogoWrapper>
              <BurgerButton
                onClick={toggleMenu}
                isActive={isMenuVisible}
                aria-label="Toggle navigation menu"
                aria-expanded={isMenuVisible}
                aria-controls="mobile-menu"
                role="button"
                tabIndex={0}
              >
                <Line />
                <Line />
                <Line />
              </BurgerButton>
              {renderLogo()}
            </LogoWrapper>
            <Menu>
              <ErrorBoundary>{renderNavbarLinks(navbar_links)}</ErrorBoundary>
            </Menu>
            <MainActionsWrapper>
              <ButtonWrapper>
                <MobileContactButton
                  to="/contact"
                  $isActive={false}
                  id="mobile-contact-navbar-link"
                >
                  Contact
                </MobileContactButton>
              </ButtonWrapper>
            </MainActionsWrapper>
          </Content>
        </HeaderWrapper>
        <HeaderBgColor ref={headerRef} $backgroundColor={theme.colors.white} $visible={show} />
      </StyledHeader>

      <MobileContent
        isVisible={isMenuVisible}
        headerHeight={headerHeight}
        id="mobile-menu"
        role="navigation"
        aria-label="Mobile navigation menu"
        aria-hidden={!isMenuVisible}
      >
        <Wrapper>
          <MobileMenu>
            <ErrorBoundary>{renderMobileNavbarLinks(navbar_links)}</ErrorBoundary>
          </MobileMenu>
        </Wrapper>
      </MobileContent>
    </>
  );
});

Header.displayName = 'Header';

export default Header;
